import { ChatWidgetConfig, ChatWidgetAPI } from './types';
import { Widget } from './widget';

/**
 * Chat Widget Public API
 *
 * Professional & Fully Customizable Chat Widget for n8n workflows
 * Provides methods to initialize, control, and customize the widget
 */
class ChatWidgetController implements ChatWidgetAPI {
  private instance: Widget | null = null;

  /**
   * Initialize the chat widget with configuration
   *
   * @param config - Widget configuration options
   * @throws Error if apiUrl is not provided
   *
   * @example
   * ChatWidget.init({
   *   apiUrl: 'https://your-n8n-instance.com/webhook/chat',
   *   title: 'AI Assistant',
   *   theme: 'light',
   *   primaryColor: '#6366f1',
   *   welcomeMessage: 'Hello! How can I help you today?',
   *   botName: 'Assistant',
   *   botAvatar: '🤖',
   *   showTimestamps: true,
   *   persistHistory: true
   * });
   */
  init(config: ChatWidgetConfig): void {
    if (this.instance) {
      console.warn('[ChatWidget] Widget already initialized. Call destroy() first to reinitialize.');
      return;
    }

    if (!config || !config.apiUrl) {
      throw new Error('[ChatWidget] Configuration error: apiUrl is required');
    }

    try {
      this.instance = new Widget(config);
      this.instance.init();
      console.info('[ChatWidget] Initialized successfully');
    } catch (error) {
      console.error('[ChatWidget] Initialization failed:', error);
      throw error;
    }
  }

  /**
   * Open the chat panel
   */
  open(): void {
    if (!this.instance) {
      console.warn('[ChatWidget] Widget not initialized. Call init() first.');
      return;
    }
    this.instance.open();
  }

  /**
   * Close the chat panel
   */
  close(): void {
    if (!this.instance) {
      console.warn('[ChatWidget] Widget not initialized. Call init() first.');
      return;
    }
    this.instance.close();
  }

  /**
   * Toggle the chat panel open/closed
   */
  toggle(): void {
    if (!this.instance) {
      console.warn('[ChatWidget] Widget not initialized. Call init() first.');
      return;
    }
    this.instance.toggle();
  }

  /**
   * Destroy the widget and remove it from the DOM
   */
  destroy(): void {
    if (!this.instance) {
      console.warn('[ChatWidget] Widget not initialized.');
      return;
    }
    this.instance.destroy();
    this.instance = null;
    console.info('[ChatWidget] Destroyed successfully');
  }

  /**
   * Send a message programmatically
   *
   * @param text - The message text to send
   */
  sendMessage(text: string): void {
    if (!this.instance) {
      console.warn('[ChatWidget] Widget not initialized. Call init() first.');
      return;
    }
    if (typeof text !== 'string' || !text.trim()) {
      console.warn('[ChatWidget] sendMessage requires a non-empty string');
      return;
    }
    this.instance.sendMessage(text);
  }

  /**
   * Clear conversation history
   */
  clearHistory(): void {
    if (!this.instance) {
      console.warn('[ChatWidget] Widget not initialized. Call init() first.');
      return;
    }
    this.instance.clearHistory();
  }

  /**
   * Update user information
   *
   * @param user - User object with id, name, email, avatar
   */
  setUser(user: ChatWidgetConfig['user']): void {
    if (!this.instance) {
      console.warn('[ChatWidget] Widget not initialized. Call init() first.');
      return;
    }
    this.instance.setUser(user);
  }

  /**
   * Update widget configuration (requires reinitialize)
   *
   * @param config - Partial configuration to update
   */
  updateConfig(config: Partial<ChatWidgetConfig>): void {
    console.warn('[ChatWidget] updateConfig requires destroy() and init() with new config');
  }
}

// Export singleton instance for IIFE (will be window.ChatWidget)
const chatWidget = new ChatWidgetController();
export default chatWidget;
